// blueprintTransferRules — the pure half of blueprint-card export/import.
//
// Split out from blueprintAssetTransfer.js so it is drivable under
// `node --test`: that module reaches CustomAssetManager (and through it
// assetManagerApi, which needs Vite's import.meta.env), so nothing testable can
// live behind that import. Same leaf discipline as nodeClipboard.js and
// graphConnectPolicy.js.
//
// What is here is exactly the part that can be WRONG in a way a browser would
// not show you: the filename, which document shape a row of each kind carries,
// and whether a file may be imported as a card at all.

import { CLIP_KINDS, exportGraphEnvelope, parseEnvelope } from './nodeClipboard.js'

/** Extension of an exported card. Doubly-suffixed so a human can see what it is. */
export const BLUEPRINT_EXPORT_EXT = '.aosbp.json'

/** Filesystem-safe fragment of a name, for the download filename only. */
export function slugify(s) {
  return String(s ?? '')
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'blueprint'
}

export function exportFileNameFor({ character, name, kind }) {
  return `${slugify(character || 'character')}__${slugify(name)}.${slugify(kind)}${BLUEPRINT_EXPORT_EXT}`
}

/**
 * A blueprint row's own graph document, plus the declarations that travel with
 * it.
 *
 * A row carries exactly ONE kind, so the events kind arrives as
 * eventNodes/eventEdges with `variables` and `functions` alongside them (see
 * blankBlueprintGraphs), while body/face arrive as plain nodes/edges with no
 * declarations of their own.
 */
export function graphAndDeclarations(row) {
  const graph = row?.graph ?? {}
  return {
    graph,
    variables: graph.variables ?? [],
    functions: graph.functions ?? {},
  }
}

export function graphNodeCount(graph) {
  return (graph?.nodes ?? graph?.eventNodes ?? []).length
}

/**
 * Validate a card for export and build its envelope.
 *
 * Throws rather than returning an empty envelope: a card export that silently
 * produced a useless file is the worst outcome available here, because the user
 * would only find out on the import side, on another character, later.
 */
export function buildCardEnvelope({ asset, row, character }) {
  const kind = asset?.graphKind ?? asset?.kind
  if (!CLIP_KINDS.includes(kind)) {
    throw new Error(`this blueprint card has no exportable graph kind (got "${kind}")`)
  }
  if (!row) throw new Error('the server returned no content for this blueprint')

  const { graph, variables, functions } = graphAndDeclarations(row)
  if (graphNodeCount(graph) === 0) {
    // An empty graph is a legitimate row (blankBlueprintGraphs mints them), but
    // exporting one writes a file that can only disappoint. Say so instead.
    throw new Error('this blueprint has no nodes yet — there is nothing to export')
  }

  return exportGraphEnvelope(graph, {
    kind,
    variables,
    functions,
    source: {
      character: character ?? null,
      blueprintAssetId: asset.rawId ?? asset.id ?? null,
      name: asset.name ?? null,
    },
  })
}

/**
 * Validate text as a whole-graph blueprint export.
 *
 * Unlike a clipboard paste, the user EXPLICITLY handed us this file, so an
 * untagged one is reported rather than silently ignored — "that is not a
 * blueprint export" is information they need.
 */
export function validateImportText(text) {
  const parsed = parseEnvelope(text)
  if (!parsed.ok) return { ok: false, reason: parsed.reason }
  if (parsed.envelope.scope !== 'graph') {
    // A loose selection is not a graph: a card minted from three unconnected
    // nodes can never be a working default. Point at the gesture that does
    // handle a subset.
    return {
      ok: false,
      reason: 'that file holds a copied node selection, not a whole graph — '
        + 'paste it into an open graph editor with Ctrl+V instead',
    }
  }
  return parsed
}

/**
 * The row document to CREATE from an envelope.
 *
 * The envelope normalised the events kind to nodes/edges on the way out;
 * denormalising happens here, once, at the write boundary — the same split
 * blueprintDoc.getGraphView / setGraphView uses server-side.
 */
export function rowDocumentFor(envelope) {
  const graph = envelope.graph ?? {}
  if (envelope.kind !== 'events') {
    return { nodes: graph.nodes ?? [], edges: graph.edges ?? [] }
  }
  return {
    eventNodes: graph.nodes ?? [],
    eventEdges: graph.edges ?? [],
    variables: envelope.declarations?.variables ?? [],
    functions: envelope.declarations?.functions ?? {},
  }
}

/** Name a newly imported card takes. */
export function importedCardName(envelope, override) {
  return override ?? envelope.source?.name ?? `Imported ${envelope.kind} graph`
}
